import { useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import type { AssistantStep } from "@/lib/types";

interface AssistantStepsProps {
  steps: AssistantStep[];
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

function StepToolList({ tools }: { tools: AssistantStep["tools"] }) {
  return (
    <div className="mt-1 space-y-0.5 pl-3">
      {tools.map((tool, index) => (
        <div key={index} className="flex items-center gap-1.5 text-xs text-cyber-muted">
          <span className={tool.status === "error" ? "text-cyber-red" : "text-cyber-muted"}>
            {tool.name}
          </span>
          {tool.title && (
            <>
              <span className="text-cyber-muted/50">&mdash;</span>
              <span className="text-cyber-muted/80">{tool.title}</span>
            </>
          )}
          {tool.durationMs != null && (
            <span className="text-cyber-muted/50">({formatDuration(tool.durationMs)})</span>
          )}
          {tool.status === "error" && <span className="text-cyber-red/70">error</span>}
        </div>
      ))}
    </div>
  );
}

export function AssistantSteps({ steps }: AssistantStepsProps) {
  const [expanded, setExpanded] = useState(false);
  const totalTools = steps.reduce((sum, step) => sum + step.tools.length, 0);
  const summary = [
    `${steps.length} step${steps.length !== 1 ? "s" : ""}`,
    totalTools > 0 ? `${totalTools} tool call${totalTools !== 1 ? "s" : ""}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="mb-2 rounded border border-cyber-border/50 bg-cyber-bg/30 text-xs">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-cyber-muted transition-colors hover:text-cyber-text"
      >
        <span className="text-[10px]">{expanded ? "▼" : "▶"}</span>
        <span>{summary}</span>
      </button>

      {expanded && (
        <div className="space-y-3 border-t border-cyber-border/30 px-3 py-2">
          {steps.map((step, index) => (
            <div key={index}>
              {step.text && (
                <div className="prose prose-sm max-w-none text-xs text-cyber-muted/90 prose-p:my-0.5">
                  <Markdown remarkPlugins={[remarkGfm]}>{step.text}</Markdown>
                </div>
              )}
              {step.tools.length > 0 && <StepToolList tools={step.tools} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
